import type { FC } from 'react';

import { useNotificationStore } from '@knocklabs/react';
import { Button, CircularProgress } from '@mui/material';
import { useHistory } from 'react-router-dom';

import {
  getCategoryFromSource,
  getCategoryIcon,
  getNotificationLink,
  getRenderedContent,
} from 'features/notifications/lib/helpers';
import { useNotificationFeed } from 'features/notifications/lib/NotificationFeedContext';
import { NoResults } from 'shared/ui/NoResults';

import { NotificationItem } from './NotificationItem';

/** The feed's items, newest first. Lays out as a column and leaves scrolling to its container. */
export const NotificationsList: FC = () => {
  const history = useHistory();
  const { feedClient } = useNotificationFeed();
  const { items, metadata, loading, networkStatus } = useNotificationStore(feedClient);

  if (loading && items.length === 0) {
    return (
      <div className="flex items-center justify-center py-12">
        <CircularProgress size={24} />
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <NoResults
        className="min-h-[400px] w-auto flex-1"
        title="No notifications"
        description="Once something important happens, you'll see it here"
      />
    );
  }

  return (
    <div className="flex flex-col gap-3">
      {items.map((item) => {
        const category = getCategoryFromSource(item.source);
        const link = getNotificationLink(category, item);

        return (
          <NotificationItem
            key={item.id}
            item={item}
            icon={getCategoryIcon(category)}
            renderedContent={getRenderedContent(item.blocks)}
            isUnread={!item.read_at}
            category={category}
            onClick={
              link
                ? () => {
                    feedClient.markAsRead(item);
                    history.push(link);
                  }
                : undefined
            }
            onMarkAsRead={(event) => {
              event.stopPropagation();
              feedClient.markAsRead(item);
            }}
            onMarkAsUnread={(event) => {
              event.stopPropagation();
              feedClient.markAsUnread(item);
            }}
            showUnreadToggle
          />
        );
      })}

      {metadata.total_count > items.length && (
        <Button variant="text" disabled={networkStatus === 'fetchMore'} onClick={() => feedClient.fetchNextPage()}>
          {networkStatus === 'fetchMore' ? <CircularProgress size={16} className="my-1" /> : 'See more'}
        </Button>
      )}
    </div>
  );
};
